import React from 'react';
import type { CaseHandoff, HandoffSeat } from '../../services/handoffApi';

const STAGE_LABEL: Record<string, string> = {
  setup: 'SETUP',
  foundation: 'FOUNDATION',
  method: 'METHOD',
  calculate: 'CALCULATE',
  attest: 'ATTEST',
  deliver: 'DELIVER',
};

const SEAT_LABEL: Record<HandoffSeat, string> = {
  data_specialist: 'DATA',
  accountant: 'ACCOUNTANT',
  attorney: 'ATTORNEY',
};
const SEAT_LABEL_COMPACT: Record<HandoffSeat, string> = {
  data_specialist: 'DATA',
  accountant: 'ACCT',
  attorney: 'ATTY',
};

/** "Dara Okafor" -> "D. Okafor"; compact -> "D.O."; single token kept as-is. */
const CREDENTIAL_SUFFIXES = new Set([
  'cpa', 'esq', 'cfe', 'cff', 'abv', 'cva', 'cfa', 'jd', 'mba', 'phd', 'pa',
  'jr', 'sr', 'ii', 'iii', 'iv',
]);

export function formatHandoffName(name: string, compact = false): string {
  // Strip punctuation per token, then drop trailing credential/suffix tokens so
  // "Dana Okafor, CPA" -> "D. Okafor", not "D. CPA".
  let parts = name
    .trim()
    .split(/\s+/)
    .map((p) => p.replace(/[.,]+$/g, '').replace(/,/g, ''))
    .filter(Boolean);
  while (parts.length > 1 && CREDENTIAL_SUFFIXES.has(parts[parts.length - 1].toLowerCase())) {
    parts = parts.slice(0, -1);
  }
  if (parts.length === 0) return '';
  if (parts.length === 1) return compact ? parts[0].charAt(0).toUpperCase() + '.' : parts[0];
  const first = parts[0].charAt(0).toUpperCase();
  const last = parts[parts.length - 1];
  return compact ? `${first}.${last.charAt(0).toUpperCase()}.` : `${first}. ${last}`;
}

/** Six-bar stepper. Done = dim grey @40%, current = the only orange, future = hollow stroke @20%. */
export function HandoffStepper({ index }: { index: number }) {
  return (
    <span className="inline-flex items-center shrink-0" style={{ gap: 2 }} aria-hidden="true">
      {[1, 2, 3, 4, 5, 6].map((n) => {
        const style: React.CSSProperties = { width: 4, height: 9, display: 'inline-block' };
        if (n === index) style.backgroundColor = 'var(--color-brand)';
        else if (n < index) {
          style.backgroundColor = 'var(--color-text-dim)';
          style.opacity = 0.4;
        } else {
          style.border = '1px solid var(--color-text-dim)';
          style.opacity = 0.2;
          style.boxSizing = 'border-box';
        }
        return <span key={n} style={style} />;
      })}
    </span>
  );
}

interface HandoffLineProps {
  handoff?: CaseHandoff | null;
  viewerSeat?: HandoffSeat;
  /** Collapsed mode: initials + short seat labels (Data Specialist queue). */
  compact?: boolean;
}

export default function HandoffLine({ handoff, viewerSeat, compact = false }: HandoffLineProps) {
  if (!handoff) return null;
  const idx = Math.min(6, Math.max(1, handoff.stage_index || 1));
  const stage = STAGE_LABEL[handoff.current_stage] || String(handoff.current_stage || '').toUpperCase();
  const mine = !!handoff.next_seat && handoff.next_seat === viewerSeat;
  const seatText = handoff.next_seat
    ? mine
      ? 'YOU'
      : (compact ? SEAT_LABEL_COMPACT : SEAT_LABEL)[handoff.next_seat]
    : null;

  const check = (tag: string, name: string) => (
    <>
      <span aria-hidden="true">·</span>
      <span className="shrink-0 whitespace-nowrap">
        <span style={{ color: 'var(--color-verified)' }} aria-hidden="true">✓</span> {tag}{' '}
        {formatHandoffName(name, compact)}
      </span>
    </>
  );

  // Full plain-text trail — shown inline when not compact, and always exposed
  // via title/aria so the compact (narrow-column) variant loses no information.
  const seatFull = handoff.next_seat ? ` → NEXT: ${mine ? 'YOU' : SEAT_LABEL[handoff.next_seat]}` : '';
  const soFull = handoff.signed_off_by ? ` · ✓ SO ${formatHandoffName(handoff.signed_off_by)}` : '';
  const atFull = handoff.attested_by ? ` · ✓ AT ${formatHandoffName(handoff.attested_by)}` : '';
  const summary = `${stage} ${idx}/6${seatFull}${soFull}${atFull}`;

  return (
    <div
      className="flex items-center gap-1.5 min-w-0 whitespace-nowrap overflow-hidden"
      style={{ fontSize: 11, lineHeight: '14px', color: 'var(--color-text-secondary)' }}
      data-testid="handoff-line"
      title={compact ? summary : undefined}
      aria-label={summary}
    >
      <HandoffStepper index={idx} />
      <span
        className={`uppercase ${compact ? 'truncate min-w-0' : 'shrink-0'}`}
        style={{ letterSpacing: '0.06em' }}
      >
        {compact ? `${idx}/6 ${stage}` : `${stage} · ${idx}/6`}
      </span>
      {/* Compact (DS narrow table) drops the NEXT/SO/AT tail — redundant with the
          Queue + Next Action columns there, and clipping it mid-token read as a
          bug. Full trail stays on the accountant seat (compact=false) + on hover. */}
      {!compact && seatText && (
        <span
          className="shrink-0"
          style={{ opacity: mine ? 1 : 0.65, fontWeight: mine ? 600 : 400, color: mine ? 'var(--color-text-primary)' : undefined }}
        >
          → NEXT: {seatText}
        </span>
      )}
      {!compact && handoff.signed_off_by && check('SO', handoff.signed_off_by)}
      {!compact && handoff.attested_by && check('AT', handoff.attested_by)}
    </div>
  );
}
